import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Plus, Filter, Search, Calendar, Users, 
  FileText, Award, Edit2, Trash2, Eye, CheckCircle, 
  XCircle, Clock, BarChart3, TrendingUp, Edit, Archive
} from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import { PILLAR_NAMES } from '../data/pillars';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AssessmentManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [assessments, setAssessments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({
    total_questions: 0,
    pillars: []
  });
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    fetchStats();
    fetchAssessments();
    fetchOrganizations();
    fetchQuestions(); // Load questions on mount
  }, []);

  const fetchStats = async () => {
    try {
      // Use enhanced API for stats with C-N-E-S-S features
      const response = await fetch(`${BACKEND_URL}/api/enhanced-assessment/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const url = filterStatus === 'all' 
        ? `${BACKEND_URL}/api/assessments`
        : `${BACKEND_URL}/api/assessments?status=${filterStatus}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setAssessments(data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to sort questions numerically (P1_Q1, P1_Q2, ... P1_Q10)
  const sortQuestionsNumerically = (questions) => {
    return [...questions].sort((a, b) => {
      const matchA = (a.question_id || '').match(/P(\d+)_Q(\d+)/);
      const matchB = (b.question_id || '').match(/P(\d+)_Q(\d+)/);
      if (matchA && matchB) {
        const pillarDiff = parseInt(matchA[1]) - parseInt(matchB[1]);
        if (pillarDiff !== 0) return pillarDiff;
        return parseInt(matchA[2]) - parseInt(matchB[2]);
      }
      return 0;
    });
  };

  const fetchQuestions = async (pillar = null) => {
    try {
      // Use enhanced API for new question format with C-N-E-S-S journey
      const url = pillar 
        ? `${BACKEND_URL}/api/enhanced-assessment/questions?pillar=${pillar}`
        : `${BACKEND_URL}/api/enhanced-assessment/questions`;
      
      const response = await fetch(url);
      const data = await response.json();
      // Enhanced API returns { questions: [...], total: X }
      const rawQuestions = data.questions || data;
      // Sort questions numerically
      setQuestions(sortQuestionsNumerically(rawQuestions));
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizations`);
      const data = await response.json();
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, [filterStatus]);

  // Calculate dashboard stats
  const dashboardStats = {
    totalAssessments: assessments.length,
    activeAssessments: assessments.filter(a => a.status === 'active').length,
    completedAssessments: assessments.filter(a => a.status === 'completed').length,
    draftAssessments: assessments.filter(a => a.status === 'draft').length
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      archived: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <Edit2 className="w-4 h-4" />,
      active: <CheckCircle className="w-4 h-4" />,
      in_progress: <Clock className="w-4 h-4" />,
      completed: <Award className="w-4 h-4" />,
      archived: <XCircle className="w-4 h-4" />
    };
    return icons[status] || <FileText className="w-4 h-4" />;
  };

  return (
    <SystemAdminLayout>
    <div className="bg-gray-50 min-h-full">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-8 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black mb-2">Assessment Engine</h1>
              <p className="text-indigo-100 text-lg">Manage assessments and question bank</p>
            </div>
            <button
              onClick={() => {
                setShowCreateModal(true);
                setSelectedAssessment(null);
              }}
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Assessment
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-4 font-semibold border-b-4 transition-all ${
                activeTab === 'dashboard'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Dashboard
              </div>
            </button>
            <button
              onClick={() => setActiveTab('assessments')}
              className={`px-4 py-4 font-semibold border-b-4 transition-all ${
                activeTab === 'assessments'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                All Assessments
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('questions');
                fetchQuestions();
              }}
              className={`px-4 py-4 font-semibold border-b-4 transition-all ${
                activeTab === 'questions'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Question Bank
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <DashboardView 
            dashboardStats={dashboardStats}
            stats={stats}
            assessments={assessments}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
          />
        )}

        {/* All Assessments Tab */}
        {activeTab === 'assessments' && (
          <AssessmentsListView
            assessments={assessments}
            loading={loading}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            setShowCreateModal={setShowCreateModal}
            setSelectedAssessment={setSelectedAssessment}
            setActiveTab={setActiveTab}
            fetchAssessments={fetchAssessments}
            backendUrl={BACKEND_URL}
          />
        )}

        {/* Question Bank Tab */}
        {activeTab === 'questions' && (
          <QuestionBankView 
            questions={questions} 
            stats={stats} 
            fetchQuestions={fetchQuestions}
            selectedQuestion={selectedQuestion}
            setSelectedQuestion={setSelectedQuestion}
          />
        )}

        {/* Assessment Detail View */}
        {activeTab === 'detail' && selectedAssessment && (
          <AssessmentDetailView 
            assessment={selectedAssessment}
            onBack={() => setActiveTab('assessments')}
            onEdit={() => {
              setShowCreateModal(true);
              setActiveTab('assessments');
            }}
          />
        )}
      </div>

      {/* Create/Edit Assessment Modal */}
      {showCreateModal && (
        <CreateAssessmentModal
          assessment={selectedAssessment}
          organizations={organizations}
          stats={stats}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedAssessment(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setSelectedAssessment(null);
            fetchAssessments();
          }}
          backendUrl={BACKEND_URL}
        />
      )}
    </div>
    </SystemAdminLayout>
  );
};

// Dashboard View Component
const DashboardView = ({ dashboardStats, stats, assessments, getStatusColor, getStatusIcon }) => {
  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-indigo-600 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-semibold mb-1">Total Assessments</p>
              <p className="text-3xl font-black text-gray-900">{dashboardStats.totalAssessments}</p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-lg">
              <FileText className="w-8 h-8 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-semibold mb-1">Active Assessments</p>
              <p className="text-3xl font-black text-gray-900">{dashboardStats.activeAssessments}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <CheckCircle className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-semibold mb-1">Completed</p>
              <p className="text-3xl font-black text-gray-900">{dashboardStats.completedAssessments}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <Award className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-600 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-semibold mb-1">Question Bank</p>
              <p className="text-3xl font-black text-gray-900">{stats.total_questions}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <BookOpen className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Pillar Distribution */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-indigo-600" />
          Question Distribution by Pillar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(stats.pillars || []).map((pillar) => (
            <div key={pillar.pillar_number} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-600">Pillar {pillar.pillar_number}</span>
                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs font-bold">
                  {pillar.total_count || pillar.question_count || 0} Q
                </span>
              </div>
              <p className="text-xs text-gray-500 line-clamp-2">{pillar.pillar_name}</p>
              {/* Show qual/quant breakdown */}
              <div className="mt-2 flex gap-2">
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  {pillar.qualitative_count || 0} Qual
                </span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  {pillar.quantitative_count || 0} Quant
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Assessments */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Assessments</h2>
        <div className="space-y-3">
          {assessments.slice(0, 5).length > 0 ? (
            assessments.slice(0, 5).map((assessment) => (
              <div key={assessment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{assessment.assessment_name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{assessment.assessment_code}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(assessment.status)}`}>
                    {getStatusIcon(assessment.status)}
                    {assessment.status}
                  </span>
                  <span className="text-sm text-gray-500">{assessment.total_questions || 0} questions</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No assessments created yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Assessments List View Component
const AssessmentsListView = ({ 
  assessments, loading, filterStatus, setFilterStatus, searchQuery, setSearchQuery,
  getStatusColor, getStatusIcon, setShowCreateModal, setSelectedAssessment, setActiveTab,
  fetchAssessments, backendUrl
}) => {
  
  const handleDelete = async (assessmentId) => {
    if (window.confirm('Are you sure you want to delete this assessment?')) {
      try {
        const response = await fetch(`${backendUrl}/api/assessments/${assessmentId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          fetchAssessments();
        }
      } catch (error) {
        console.error('Error deleting assessment:', error);
      }
    }
  };

  const filteredAssessments = assessments.filter(a => 
    a.assessment_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.assessment_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search assessments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assessments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading assessments...</p>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Assessments Found</h3>
            <p className="text-gray-500 mb-6">
              {assessments.length === 0 ? 'Create your first assessment to get started' : 'Try adjusting your search or filters'}
            </p>
            {assessments.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-all inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Assessment
              </button>
            )}
          </div>
        ) : (
          filteredAssessments.map((assessment) => (
            <div key={assessment.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{assessment.assessment_name}</h3>
                      <p className="text-sm text-gray-500 font-mono">{assessment.assessment_code}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(assessment.status)}`}>
                      {getStatusIcon(assessment.status)}
                      {assessment.status}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{assessment.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {assessment.start_date} - {assessment.end_date}
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {assessment.total_questions || 0} questions
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {assessment.organizations?.length || 0} organizations
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedAssessment(assessment);
                      setActiveTab('detail');
                    }}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    title="View Details"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAssessment(assessment);
                      setShowCreateModal(true);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                    title="Edit"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(assessment.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Question Bank View Component
const QuestionBankView = ({ questions, stats, fetchQuestions, selectedQuestion, setSelectedQuestion }) => {
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleToggleActive = async (questionId, currentStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions/${questionId}/toggle-active?is_active=${!currentStatus}`, {
        method: 'PATCH'
      });
      if (response.ok) {
        fetchQuestions(selectedPillar);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDelete = async (questionId) => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions/${questionId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          fetchQuestions(selectedPillar);
        }
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const displayQuestions = selectedPillar 
    ? questions.filter(q => q.pillar_number === selectedPillar)
    : questions;

  return (
    <div className="space-y-6">
      {/* Pillar Filter */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Filter by Pillar</h3>
          <button
            onClick={() => {
              setShowAddModal(true);
              setEditingQuestion(null);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Question
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setSelectedPillar(null);
              fetchQuestions();
            }}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              selectedPillar === null
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Pillars ({stats.total_questions})
          </button>
          {(stats.pillars || []).map((pillar) => (
            <button
              key={pillar.pillar_number}
              onClick={() => {
                setSelectedPillar(pillar.pillar_number);
                fetchQuestions(pillar.pillar_number);
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                selectedPillar === pillar.pillar_number
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pillar {pillar.pillar_number} ({pillar.total_count || pillar.question_count || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {displayQuestions.length > 0 ? (
          displayQuestions.map((question) => (
            <div key={question.question_id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border border-gray-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                  <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg text-sm font-bold">
                    {question.question_id}
                  </span>
                  <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-lg text-sm font-semibold">
                    {question.question_type || question.input_type}
                  </span>
                  {/* Company Size Tags */}
                  {question.company_size && (
                    <div className="flex gap-1">
                      {(Array.isArray(question.company_size) ? question.company_size : [question.company_size]).slice(0, 3).map(size => (
                        <span key={size} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs font-medium">
                          {size}
                        </span>
                      ))}
                      {Array.isArray(question.company_size) && question.company_size.length > 3 && (
                        <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs font-medium">
                          +{question.company_size.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedQuestion(question)}
                    className="text-indigo-600 hover:text-indigo-800 font-semibold text-sm flex items-center gap-1 px-3 py-1 hover:bg-indigo-50 rounded transition-all"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <button
                    onClick={() => {
                      setEditingQuestion(question);
                      setShowEditModal(true);
                    }}
                    className="text-amber-600 hover:text-amber-800 font-semibold text-sm flex items-center gap-1 px-3 py-1 hover:bg-amber-50 rounded transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(question.question_id, question.is_active)}
                    className={`font-semibold text-sm flex items-center gap-1 px-3 py-1 rounded transition-all ${
                      question.is_active !== false
                        ? 'text-green-600 hover:text-green-800 hover:bg-green-50'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                    title={question.is_active !== false ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {question.is_active !== false ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(question.question_id)}
                    className="text-red-500 hover:text-red-700 font-semibold text-sm flex items-center gap-1 px-3 py-1 hover:bg-red-50 rounded transition-all"
                    title="Delete question"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{question.question_text}</h3>
              <p className="text-sm text-gray-600 mb-3 flex items-center gap-2">
                <span className="font-medium">{question.pillar_name}</span>
                {question.sub_pillar && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">{question.sub_pillar}</span>
                  </>
                )}
              </p>
              
              {/* Enhanced Question Features */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <span className="flex items-center gap-1 text-gray-600">
                  <BookOpen className="w-3 h-3" />
                  {question.rubric ? Object.keys(question.rubric).length : 5} C-N-E-S-S levels
                </span>
                {question.why_this_matters?.enabled && (
                  <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded flex items-center gap-1">
                    ❓ Why It Matters
                  </span>
                )}
                {question.quick_win?.enabled && (
                  <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded flex items-center gap-1">
                    💡 Quick Win
                  </span>
                )}
                {question.best_practices?.enabled && (
                  <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded flex items-center gap-1">
                    ⭐ Best Practices
                  </span>
                )}
                {question.continuous_improvement?.enabled && (
                  <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                    ✨ CI Prompt
                  </span>
                )}
                {question.evidence_type && question.evidence_type !== 'NONE' && (
                  <span className={`px-2 py-1 rounded flex items-center gap-1 ${
                    question.evidence_type === 'REQUIRED' 
                      ? 'bg-red-50 text-red-700' 
                      : 'bg-gray-50 text-gray-600'
                  }`}>
                    📎 Evidence {question.evidence_type}
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No questions available</p>
          </div>
        )}
      </div>

      {/* Question Detail Modal */}
      {selectedQuestion && (
        <QuestionDetailModal
          question={selectedQuestion}
          onClose={() => setSelectedQuestion(null)}
        />
      )}

      {/* Edit Question Modal */}
      {showEditModal && editingQuestion && (
        <EditQuestionModal
          question={editingQuestion}
          onClose={() => {
            setShowEditModal(false);
            setEditingQuestion(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditingQuestion(null);
            fetchQuestions(selectedPillar);
          }}
        />
      )}

      {/* Add Question Modal */}
      {showAddModal && (
        <AddQuestionModal
          selectedPillar={selectedPillar}
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            fetchQuestions(selectedPillar);
          }}
        />
      )}
    </div>
  );
};

// Assessment Detail View Component
const AssessmentDetailView = ({ assessment, onBack, onEdit }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-2 transition-all"
        >
          ← Back to Assessments
        </button>
        <button
          onClick={onEdit}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-all flex items-center gap-2"
        >
          <Edit2 className="w-4 h-4" />
          Edit Assessment
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-3xl font-black text-gray-900 mb-2">{assessment.assessment_name}</h2>
            <p className="text-gray-500 font-mono">{assessment.assessment_code}</p>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
            assessment.status === 'active' ? 'bg-green-100 text-green-700' :
            assessment.status === 'draft' ? 'bg-gray-100 text-gray-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {assessment.status}
          </span>
        </div>

        <p className="text-gray-600 mb-6">{assessment.description}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Timeline</p>
            <p className="font-semibold text-gray-900">{assessment.start_date} to {assessment.end_date}</p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Questions</p>
            <p className="font-semibold text-gray-900">{assessment.total_questions || 0} questions</p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Organizations</p>
            <p className="font-semibold text-gray-900">{assessment.organizations?.length || 0} assigned</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-bold text-gray-900 mb-4">Included Pillars</h3>
          <div className="flex flex-wrap gap-2">
            {assessment.included_pillars?.map((pillar) => (
              <span key={pillar} className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg font-semibold">
                Pillar {pillar}
              </span>
            ))}
          </div>
        </div>

        {assessment.organizations && assessment.organizations.length > 0 && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="font-bold text-gray-900 mb-4">Assigned Organizations</h3>
            <div className="space-y-2">
              {assessment.organizations.map((org) => (
                <div key={org.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span className="font-semibold text-gray-900">{org.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Create/Edit Assessment Modal Component
const CreateAssessmentModal = ({ assessment, organizations, stats, onClose, onSave, backendUrl }) => {
  const [formData, setFormData] = useState({
    assessment_name: assessment?.assessment_name || '',
    description: assessment?.description || '',
    included_pillars: assessment?.included_pillars || [],
    start_date: assessment?.start_date || '',
    end_date: assessment?.end_date || '',
    assigned_organization_ids: assessment?.assigned_organization_ids || [],
    created_by_user_id: 'super_admin_2025'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handlePillarToggle = (pillar) => {
    setFormData(prev => ({
      ...prev,
      included_pillars: prev.included_pillars.includes(pillar)
        ? prev.included_pillars.filter(p => p !== pillar)
        : [...prev.included_pillars, pillar].sort()
    }));
  };

  const handleOrgToggle = (orgId) => {
    setFormData(prev => ({
      ...prev,
      assigned_organization_ids: prev.assigned_organization_ids.includes(orgId)
        ? prev.assigned_organization_ids.filter(id => id !== orgId)
        : [...prev.assigned_organization_ids, orgId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = assessment
        ? `${backendUrl}/api/assessments/${assessment.id}`
        : `${backendUrl}/api/assessments`;
      
      const method = assessment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save assessment');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl z-10">
          <h2 className="text-2xl font-black">
            {assessment ? 'Edit Assessment' : 'Create New Assessment'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assessment Name *
            </label>
            <input
              type="text"
              required
              value={formData.assessment_name}
              onChange={(e) => setFormData({ ...formData, assessment_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Q1 2025 Comprehensive Assessment"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Describe the purpose and scope of this assessment..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Pillars * (Choose at least one)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((pillar) => (
                <button
                  key={pillar}
                  type="button"
                  onClick={() => handlePillarToggle(pillar)}
                  className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                    formData.included_pillars.includes(pillar)
                      ? 'bg-indigo-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Pillar {pillar}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Assign to Organizations (Optional)
            </label>
            <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
              {organizations && organizations.length > 0 ? (
                organizations.map((org) => (
                  <div
                    key={org.id}
                    onClick={() => handleOrgToggle(org.id)}
                    className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-all ${
                      formData.assigned_organization_ids.includes(org.id) ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.assigned_organization_ids.includes(org.id)}
                        onChange={() => {}}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-semibold text-gray-900">{org.name}</div>
                        <div className="text-sm text-gray-500">{org.industry}</div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No organizations available
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || formData.included_pillars.length === 0}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>{assessment ? 'Update Assessment' : 'Create Assessment'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Question Detail Modal Component
const QuestionDetailModal = ({ question, onClose }) => {
  // Support enhanced rubric format with gamified C-N-E-S-S labels
  const hasRubric = question.rubric && typeof question.rubric === 'object';
  const rubricLevels = hasRubric ? Object.entries(question.rubric).sort((a, b) => parseInt(b[0]) - parseInt(a[0])) : [];
  
  // C-N-E-S-S Journey styling
  const getLevelStyle = (level) => {
    const styles = {
      '5': { 
        bg: 'bg-gradient-to-r from-amber-50 to-yellow-50', 
        border: 'border-amber-400', 
        text: 'text-amber-900', 
        headerBg: 'bg-gradient-to-r from-amber-500 to-yellow-500',
        icon: '🏆', 
        letter: 'S',
        label: 'EXEMPLARY', 
        journey: 'Setting the Standard',
        score: '100',
        scoreRange: '90-100',
        description: 'Industry-leading; embedded in organizational DNA'
      },
      '4': { 
        bg: 'bg-gradient-to-r from-purple-50 to-violet-50', 
        border: 'border-purple-400', 
        text: 'text-purple-900',
        headerBg: 'bg-gradient-to-r from-purple-500 to-violet-500', 
        icon: '🌲', 
        letter: 'S',
        label: 'LEADING', 
        journey: 'Leading the Way',
        score: '80',
        scoreRange: '70-89',
        description: 'Comprehensive systems; high consistency'
      },
      '3': { 
        bg: 'bg-gradient-to-r from-emerald-50 to-green-50', 
        border: 'border-emerald-400', 
        text: 'text-emerald-900',
        headerBg: 'bg-gradient-to-r from-emerald-500 to-green-500', 
        icon: '🌳', 
        letter: 'E',
        label: 'GROWING', 
        journey: 'Growing Strong',
        score: '60',
        scoreRange: '50-69',
        description: 'Documented practices; moderate consistency'
      },
      '2': { 
        bg: 'bg-gradient-to-r from-blue-50 to-sky-50', 
        border: 'border-blue-400', 
        text: 'text-blue-900',
        headerBg: 'bg-gradient-to-r from-blue-500 to-sky-500', 
        icon: '🌿', 
        letter: 'N',
        label: 'BUILDING', 
        journey: 'Building Foundation',
        score: '40',
        scoreRange: '30-49',
        description: 'Basic awareness; developing implementation'
      },
      '1': { 
        bg: 'bg-gradient-to-r from-slate-50 to-gray-50', 
        border: 'border-slate-400', 
        text: 'text-slate-900',
        headerBg: 'bg-gradient-to-r from-slate-500 to-gray-500', 
        icon: '🌱', 
        letter: 'C',
        label: 'STARTING', 
        journey: 'Starting the Journey',
        score: '20',
        scoreRange: '0-29',
        description: 'Beginning to explore this area'
      }
    };
    return styles[level] || styles['3'];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-700 to-indigo-800 text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-bold">
                  {question.question_id}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                  question.question_type === 'quantitative' 
                    ? 'bg-blue-400 text-blue-900' 
                    : 'bg-green-400 text-green-900'
                }`}>
                  {question.question_type?.toUpperCase() || 'QUALITATIVE'}
                </span>
              </div>
              <h2 className="text-xl font-black">{question.pillar_name}</h2>
              <p className="text-purple-200 text-sm mt-1">Sub-pillar: {question.sub_pillar}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
            >
              <XCircle className="w-8 h-8" />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Question Section */}
          <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-indigo-500">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-indigo-600 font-bold text-sm uppercase tracking-wide">Assessment Question</span>
              <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-bold">
                Weight: {question.weight} points
              </span>
            </div>
            <p className="text-gray-900 text-lg leading-relaxed font-medium">{question.question_text}</p>
          </div>

          {/* Evidence Required Section */}
          {question.evidence_required && question.evidence_required.length > 0 && (
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="font-bold text-amber-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Required Evidence & Documentation
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {question.evidence_required.map((evidence, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-amber-800">
                    <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>{evidence}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Enhanced Question Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Why This Matters */}
            {question.why_this_matters?.enabled && question.why_this_matters?.text && (
              <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                  ❓ Why This Matters
                </h4>
                <p className="text-indigo-800 text-sm">{question.why_this_matters.text}</p>
                {question.why_this_matters.show_for && (
                  <p className="text-indigo-600 text-xs mt-2">
                    Shown for: {question.why_this_matters.show_for.join(', ')}
                  </p>
                )}
              </div>
            )}

            {/* Quick Win */}
            {question.quick_win?.enabled && question.quick_win?.text && (
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                  💡 Quick Win Suggestion
                </h4>
                <p className="text-emerald-800 text-sm">{question.quick_win.text}</p>
                {question.quick_win.show_for && (
                  <p className="text-emerald-600 text-xs mt-2">
                    Shown for: {question.quick_win.show_for.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Best Practices & CI Prompts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {question.best_practices?.enabled && (
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                  ⭐ Best Practices Prompt
                </h4>
                <p className="text-amber-800 text-sm font-medium">{question.best_practices.prompt}</p>
                <p className="text-amber-600 text-xs mt-2 italic">{question.best_practices.placeholder}</p>
              </div>
            )}

            {question.continuous_improvement?.enabled && (
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                <h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                  ✨ Continuous Improvement Prompt
                </h4>
                <p className="text-purple-800 text-sm font-medium">{question.continuous_improvement.prompt}</p>
                <p className="text-purple-600 text-xs mt-2 italic">{question.continuous_improvement.placeholder}</p>
              </div>
            )}
          </div>

          {/* C-N-E-S-S Scoring Journey */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-black text-gray-900 text-xl flex items-center gap-2">
                <Award className="w-6 h-6 text-indigo-600" />
                C-N-E-S-S Scoring Journey
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="font-medium">Your Journey:</span>
                {['C', 'N', 'E', 'S', 'S'].map((letter, i) => (
                  <span key={i} className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold">
                    {letter}
                  </span>
                ))}
              </div>
            </div>
            
            {hasRubric ? (
              <div className="space-y-4">
                {rubricLevels.map(([level, data]) => {
                  const style = getLevelStyle(level);
                  return (
                    <div key={level} className={`${style.bg} border-2 ${style.border} rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
                      {/* Level Header */}
                      <div className={`${style.headerBg} text-white px-5 py-3 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <span className="text-2xl">{style.icon}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="w-8 h-8 bg-white bg-opacity-30 rounded-lg flex items-center justify-center text-xl font-black">
                                {style.letter}
                              </span>
                              <p className="font-black text-lg">{data.gamified_label || data.short_label || style.journey}</p>
                            </div>
                            <p className="text-white text-opacity-80 text-sm">Level {level}: {data.label || style.label}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-2xl">{style.score}</p>
                          <p className="text-white text-opacity-80 text-xs">points</p>
                        </div>
                      </div>
                      {/* Level Description */}
                      <div className="p-5">
                        <p className="text-gray-800 leading-relaxed">{data.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : question.scoring_criteria?.length > 0 ? (
              // Legacy format support
              <div className="space-y-6">
                {question.scoring_criteria.map((criterion, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-xl p-6 bg-white shadow-sm">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                      <h4 className="font-black text-gray-900 text-lg">
                        Criterion {criterion.criterion_number}: {criterion.name}
                      </h4>
                      <span className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-lg font-bold">
                        Weight: {criterion.weight}%
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                        <p className="font-bold text-green-800 mb-1">✨ EXCEPTIONAL (90-100 points)</p>
                        <p className="text-gray-700">{criterion.exceptional_90to100}</p>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                        <p className="font-bold text-blue-800 mb-1">💪 STRONG (70-89 points)</p>
                        <p className="text-gray-700">{criterion.strong_70to89}</p>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                        <p className="font-bold text-yellow-800 mb-1">🌱 DEVELOPING (50-69 points)</p>
                        <p className="text-gray-700">{criterion.developing_50to69}</p>
                      </div>
                      <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4">
                        <p className="font-bold text-orange-800 mb-1">🌟 EMERGING (30-49 points)</p>
                        <p className="text-gray-700">{criterion.emerging_30to49}</p>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
                        <p className="font-bold text-red-800 mb-1">⚠️ INSUFFICIENT (0-29 points)</p>
                        <p className="text-gray-700">{criterion.insufficient_0to29}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No rubric data available for this question.</p>
              </div>
            )}
          </div>

          {/* Scoring Summary */}
          <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
            <h4 className="font-bold text-indigo-900 mb-4">Scoring Summary</h4>
            <div className="grid grid-cols-5 gap-3">
              {['5', '4', '3', '2', '1'].map((lvl) => {
                const s = getLevelStyle(lvl);
                return (
                  <div key={lvl} className={`text-center p-3 rounded-lg ${s.bg} border ${s.border}`}>
                    <p className="text-2xl mb-1">{s.icon}</p>
                    <p className={`font-bold text-sm ${s.text}`}>{s.label}</p>
                    <p className={`text-lg font-black ${s.text}`}>{s.score} pts</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-100 p-6 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-4 bg-indigo-600 text-white rounded-xl font-bold text-lg hover:bg-indigo-700 transition-all shadow-lg"
          >
            Close Rubric View
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Question Modal Component
const EditQuestionModal = ({ question, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    question_text: question.question_text || '',
    help_text: question.help_text || '',
    question_type: question.question_type || 'qualitative',
    file_upload_required: question.file_upload_required || false,
    scoring_criteria: question.scoring_criteria || [],
    is_active: question.is_active !== undefined ? question.is_active : true,
    // Rubric fields for C-N-E-S-S
    rubric: question.rubric || {
      "1": { label: "Beginning", description: "", gamified_label: "🌱 Starting the Journey", short_label: "Starting" },
      "2": { label: "Developing", description: "", gamified_label: "🌿 Building Foundation", short_label: "Building" },
      "3": { label: "Established", description: "", gamified_label: "🌳 Growing Strong", short_label: "Growing" },
      "4": { label: "Advanced", description: "", gamified_label: "🌲 Leading the Way", short_label: "Leading" },
      "5": { label: "Exemplary", description: "", gamified_label: "🏆 Setting the Standard", short_label: "Exemplary" }
    },
    quick_win: question.quick_win || '',
    why_this_matters: question.why_this_matters || '',
    applicable_to: question.applicable_to || ['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'],
    sub_pillar: question.sub_pillar || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [subPillars, setSubPillars] = useState([]);

  // Fetch sub-pillars for this pillar
  useEffect(() => {
    const fetchSubPillars = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pillars-with-sub-pillars`);
        if (response.ok) {
          const data = await response.json();
          const pillarData = data.find(p => p.pillar_number === question.pillar_number);
          if (pillarData && pillarData.sub_pillars) {
            setSubPillars(pillarData.sub_pillars);
            
            // Auto-match current sub_pillar with correct case from master data
            const currentSubPillar = question.sub_pillar || '';
            if (currentSubPillar) {
              const matchedSubPillar = pillarData.sub_pillars.find(
                sp => (typeof sp === 'string' ? sp : (sp.name || sp.title || '')).toLowerCase() === currentSubPillar.toLowerCase()
              );
              if (matchedSubPillar) {
                const matchedValue = typeof matchedSubPillar === 'string' ? matchedSubPillar : (matchedSubPillar.name || matchedSubPillar.title);
                setFormData(prev => ({ ...prev, sub_pillar: matchedValue }));
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching sub-pillars:', err);
      }
    };
    fetchSubPillars();
  }, [question.pillar_number, question.sub_pillar]);

  const toggleOrgType = (orgType) => {
    setFormData(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(orgType)
        ? prev.applicable_to.filter(t => t !== orgType)
        : [...prev.applicable_to, orgType]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    
    try {
      // Build rubric_levels from rubric
      const rubric_levels = {
        C: { level: 1, label: "Starting the Journey", description: formData.rubric["1"]?.description || "" },
        N: { level: 2, label: "Building Foundation", description: formData.rubric["2"]?.description || "" },
        E: { level: 3, label: "Growing Strong", description: formData.rubric["3"]?.description || "" },
        S1: { level: 4, label: "Leading the Way", description: formData.rubric["4"]?.description || "" },
        S2: { level: 5, label: "Setting the Standard", description: formData.rubric["5"]?.description || "" }
      };

      // Build dropdown_options
      const dropdown_options = [1, 2, 3, 4, 5].map(i => 
        `${i}. ${formData.rubric[String(i)]?.label || ''} - ${formData.rubric[String(i)]?.description || ''}`
      );

      const payload = {
        ...formData,
        rubric_levels,
        dropdown_options,
        applicable_to: formData.applicable_to,
        sub_pillar: formData.sub_pillar
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions/${question.question_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      
      if (response.ok) {
        setSuccessMsg('Question saved successfully!');
        setTimeout(() => onSave(), 1000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update question');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateRubricLevel = (level, field, value) => {
    setFormData({
      ...formData,
      rubric: {
        ...formData.rubric,
        [level]: {
          ...formData.rubric[level],
          [field]: value
        }
      }
    });
  };

  const updateCriterion = (index, field, value) => {
    const newCriteria = [...formData.scoring_criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setFormData({ ...formData, scoring_criteria: newCriteria });
  };

  const rubricLevels = [
    { key: "1", icon: "🌱", label: "C - Starting the Journey", color: "gray" },
    { key: "2", icon: "🌿", label: "N - Building Foundation", color: "blue" },
    { key: "3", icon: "🌳", label: "E - Growing Strong", color: "green" },
    { key: "4", icon: "🌲", label: "S - Leading the Way", color: "purple" },
    { key: "5", icon: "🏆", label: "S - Setting the Standard", color: "amber" }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black">Edit Question</h2>
              <p className="text-blue-100">{question.question_id} - {question.pillar_name}</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {successMsg}
            </div>
          )}

          {/* Question Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question Text *
            </label>
            <textarea
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              placeholder="Enter question text..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
          </div>

          {/* Question Type & Active Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Question Type</label>
              <select
                value={formData.question_type}
                onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="qualitative">Qualitative</option>
                <option value="quantitative">Quantitative</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Sub-Pillar Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📂 Sub-Pillar
            </label>
            <select
              value={formData.sub_pillar}
              onChange={(e) => setFormData({ ...formData, sub_pillar: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Sub-Pillar --</option>
              {subPillars.map((sp, idx) => (
                <option key={idx} value={typeof sp === 'string' ? sp : (sp.name || sp.title)}>
                  {typeof sp === 'string' ? sp : (sp.name || sp.title)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Current: {formData.sub_pillar || 'Not assigned'}
            </p>
          </div>

          {/* Organization Type Applicability */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-xl">🏢</span> Applicable Organization Types
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select which organization sizes this question should be shown to:
            </p>
            <div className="grid grid-cols-5 gap-3">
              {['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'].map((orgType) => (
                <button
                  key={orgType}
                  type="button"
                  onClick={() => toggleOrgType(orgType)}
                  className={`p-3 rounded-xl border-2 font-bold text-sm transition-all ${
                    formData.applicable_to.includes(orgType)
                      ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                      : 'bg-gray-50 border-gray-300 text-gray-400 hover:border-gray-400'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs ${
                      formData.applicable_to.includes(orgType)
                        ? 'bg-indigo-500 border-indigo-500 text-white'
                        : 'bg-white border-gray-300'
                    }`}>
                      {formData.applicable_to.includes(orgType) ? '✓' : ''}
                    </span>
                    {orgType}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* C-N-E-S-S Rubric Levels */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-xl">📊</span> C-N-E-S-S Rubric Levels
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Define what each scoring level means for this question. Users will select one of these options.
            </p>
            <div className="space-y-4">
              {rubricLevels.map(({ key, icon, label, color }) => (
                <div key={key} className={`p-4 rounded-lg border-2 border-${color}-200 bg-${color}-50`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{icon}</span>
                    <span className="font-bold text-gray-800">{label}</span>
                  </div>
                  <textarea
                    value={formData.rubric[key]?.description || ''}
                    onChange={(e) => updateRubricLevel(key, 'description', e.target.value)}
                    placeholder={`Describe what "${label}" looks like for this question...`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Quick Win & Why It Matters */}
          <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💡 Quick Win Tip (Optional)
              </label>
              <textarea
                value={typeof formData.quick_win === 'string' ? formData.quick_win : formData.quick_win?.text || ''}
                onChange={(e) => setFormData({ ...formData, quick_win: e.target.value })}
                placeholder="A simple action users can take to improve..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ❓ Why It Matters (Optional)
              </label>
              <textarea
                value={typeof formData.why_this_matters === 'string' ? formData.why_this_matters : formData.why_this_matters?.text || ''}
                onChange={(e) => setFormData({ ...formData, why_this_matters: e.target.value })}
                placeholder="Explain why this matters to the organization..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={2}
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add Question Modal Component
const AddQuestionModal = ({ selectedPillar, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    pillar_number: selectedPillar || 1,
    pillar_name: '',
    sub_pillar: '',
    question_type: 'qualitative',
    question_text: '',
    help_text: '',
    file_upload_required: false,
    applicable_to: ['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'],
    scoring_criteria: [
      { criterion_number: 1, name: '', weight: 33, exceptional_90to100: '', strong_70to89: '', developing_50to69: '', emerging_30to49: '', insufficient_0to29: '' },
      { criterion_number: 2, name: '', weight: 33, exceptional_90to100: '', strong_70to89: '', developing_50to69: '', emerging_30to49: '', insufficient_0to29: '' },
      { criterion_number: 3, name: '', weight: 34, exceptional_90to100: '', strong_70to89: '', developing_50to69: '', emerging_30to49: '', insufficient_0to29: '' }
    ],
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [subPillars, setSubPillars] = useState([]);

  const pillarNames = PILLAR_NAMES;

  // Fetch sub-pillars when pillar changes
  useEffect(() => {
    const fetchSubPillars = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pillars-with-sub-pillars`);
        if (response.ok) {
          const data = await response.json();
          const pillarData = data.find(p => p.pillar_number === formData.pillar_number);
          if (pillarData && pillarData.sub_pillars) {
            setSubPillars(pillarData.sub_pillars);
          } else {
            setSubPillars([]);
          }
        }
      } catch (err) {
        console.error('Error fetching sub-pillars:', err);
      }
    };
    fetchSubPillars();
    if (formData.pillar_number) {
      setFormData(prev => ({ ...prev, pillar_name: pillarNames[prev.pillar_number] || '' }));
    }
  }, [formData.pillar_number]);

  const toggleOrgType = (orgType) => {
    setFormData(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(orgType)
        ? prev.applicable_to.filter(t => t !== orgType)
        : [...prev.applicable_to, orgType]
    }));
  };

  const handleSave = async () => {
    if (!formData.question_text) {
      setError('Question text is required');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );
      
      if (response.ok) {
        onSave();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create question');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateCriterion = (index, field, value) => {
    const newCriteria = [...formData.scoring_criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setFormData({ ...formData, scoring_criteria: newCriteria });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black">Add New Question</h2>
              <p className="text-green-100">Create a new assessment question</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Pillar Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Select Pillar *</label>
            <select
              value={formData.pillar_number}
              onChange={(e) => setFormData({ ...formData, pillar_number: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {Object.entries(pillarNames).map(([num, name]) => (
                <option key={num} value={num}>Pillar {num}: {name}</option>
              ))}
            </select>
          </div>

          {/* Sub-Pillar Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">📂 Sub-Pillar</label>
            <select
              value={formData.sub_pillar}
              onChange={(e) => setFormData({ ...formData, sub_pillar: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">-- Select Sub-Pillar --</option>
              {subPillars.map((sp, idx) => (
                <option key={idx} value={typeof sp === 'string' ? sp : (sp.name || sp.title)}>
                  {typeof sp === 'string' ? sp : (sp.name || sp.title)}
                </option>
              ))}
            </select>
          </div>

          {/* Organization Type Applicability */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">🏢 Applicable Organization Types</label>
            <div className="grid grid-cols-5 gap-2">
              {['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'].map((orgType) => (
                <button
                  key={orgType}
                  type="button"
                  onClick={() => toggleOrgType(orgType)}
                  className={`p-2 rounded-lg border-2 font-bold text-xs transition-all ${
                    formData.applicable_to.includes(orgType)
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : 'bg-gray-50 border-gray-300 text-gray-400'
                  }`}
                >
                  {formData.applicable_to.includes(orgType) ? '✓ ' : ''}{orgType}
                </button>
              ))}
            </div>
          </div>

          {/* Question Text with RichTextEditor */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Question Text *</label>
            <div className="border border-gray-300 rounded-lg">
              <RichTextEditor
                value={formData.question_text}
                onChange={(value) => setFormData({ ...formData, question_text: value })}
                placeholder="Enter question text..."
              />
            </div>
          </div>

          {/* Help Text with RichTextEditor */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Help Text (Optional)</label>
            <div className="border border-gray-300 rounded-lg">
              <RichTextEditor
                value={formData.help_text}
                onChange={(value) => setFormData({ ...formData, help_text: value })}
                placeholder="Enter help text..."
              />
            </div>
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Question Type</label>
            <select
              value={formData.question_type}
              onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="qualitative">Qualitative</option>
              <option value="quantitative">Quantitative</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>

          {/* Scoring Criteria */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-bold text-gray-900 mb-4">Scoring Criteria & Rubrics (3 criteria)</h3>
            {formData.scoring_criteria.map((criterion, index) => (
              <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <input
                    type="text"
                    value={criterion.name}
                    onChange={(e) => updateCriterion(index, 'name', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-semibold"
                    placeholder={`Criterion ${index + 1} Name *`}
                  />
                  <input
                    type="number"
                    value={criterion.weight}
                    onChange={(e) => updateCriterion(index, 'weight', parseInt(e.target.value))}
                    className="w-20 ml-3 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <span className="ml-1 text-gray-600">%</span>
                </div>
                
                <div className="space-y-3">
                  {['exceptional_90to100', 'strong_70to89', 'developing_50to69', 'emerging_30to49', 'insufficient_0to29'].map((level, levelIdx) => {
                    const labels = ['Exceptional (90-100)', 'Strong (70-89)', 'Developing (50-69)', 'Emerging (30-49)', 'Insufficient (0-29)'];
                    return (
                      <div key={level}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">{labels[levelIdx]}</label>
                        <div className="border border-gray-200 rounded-lg">
                          <RichTextEditor
                            value={criterion[level] || ''}
                            onChange={(value) => updateCriterion(index, level, value)}
                            placeholder={`Describe ${labels[levelIdx]} performance...`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Question'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentManagement;
